import { useEffect, useState } from "react";
import { api, fileUrl, formatApiError, API } from "../lib/api";
import { PageHeader, Card, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { PLANOS_CONFIG } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Blueprint, Plus, Trash, FileText, Printer, Eye, CheckCircle, Clock, ClockCounterClockwise, GitBranch, SealCheck, Columns, Folder, CaretRight, PaintBrush, Rows, ListDashes, PencilSimple } from "@phosphor-icons/react";
import { AnnotationEditor } from "../components/AnnotationEditor";
import { CategoryField } from "../components/CategoryField";

export default function ProjectDocs({ config = PLANOS_CONFIG }) {
  const GROUPS = config.groups;
  const SUGGESTIONS = config.suggestions;
  const SECTION = config.section || "planos";
  const DEFAULT_GROUP = GROUPS[0].value;
  const { user } = useAuth();
  const isManager = true;
  const canDelete = ["admin", "gerente_general", "gerente_area"].includes(user?.role) || !!user?.can_delete;
  const canApprove = ["admin", "gerente_general"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [group, setGroup] = useState(DEFAULT_GROUP);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", doc_group: DEFAULT_GROUP, specialty: "" });
  const [file, setFile] = useState(null);
  const [revFor, setRevFor] = useState(null);
  const [revNote, setRevNote] = useState("");
  const [revFile, setRevFile] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanForm, setScanForm] = useState({ name: "", doc_group: DEFAULT_GROUP, specialty: "" });
  const [scanFiles, setScanFiles] = useState([]);
  const [scanBusy, setScanBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ doc_group: DEFAULT_GROUP, specialty: "" });
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const saveBulk = async (e) => {
    e.preventDefault();
    if (!bulkFiles || bulkFiles.length === 0) return toast.error("Selecciona una carpeta o varios archivos");
    setBulkBusy(true);
    const fd = new FormData();
    fd.append("doc_group", bulkForm.doc_group); fd.append("specialty", bulkForm.specialty);
    [...bulkFiles].forEach((f) => fd.append("files", f));
    try {
      const r = await api.post("/projectdocs/bulk", fd);
      toast.success(`${r.data.count} documento(s) subido(s)`); setBulkOpen(false);
      setBulkForm({ doc_group: group, specialty: "" }); setBulkFiles([]); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const saveScan = async (e) => {
    e.preventDefault();
    if (!scanFiles || scanFiles.length === 0) return toast.error("Selecciona una o más fotos");
    setScanBusy(true);
    const fd = new FormData();
    fd.append("name", scanForm.name); fd.append("doc_group", scanForm.doc_group); fd.append("specialty", scanForm.specialty);
    [...scanFiles].forEach((f) => fd.append("files", f));
    try {
      await api.post("/projectdocs/scan", fd);
      toast.success("Documento escaneado a PDF"); setScanOpen(false);
      setScanForm({ name: "", doc_group: group, specialty: "" }); setScanFiles([]); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setScanBusy(false); }
  };
  const [compareFor, setCompareFor] = useState(null);
  const [revA, setRevA] = useState(null);
  const [revB, setRevB] = useState(null);

  const openCompare = (d) => {
    const vs = d.versions || [];
    setCompareFor(d);
    setRevA(vs[vs.length - 2]?.rev ?? vs[0]?.rev);
    setRevB(vs[vs.length - 1]?.rev);
  };

  const [folders, setFolders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [foldersLayout, setFoldersLayout] = useState("h");
  const [folderFilter, setFolderFilter] = useState("");
  const [dragId, setDragId] = useState(null);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [folderBusy, setFolderBusy] = useState(false);
  const [annotateFor, setAnnotateFor] = useState(null);
  const [annBusy, setAnnBusy] = useState(false);
  const isImageDoc = (d) => /image\//.test(d.content_type || "") || /\.(png|jpe?g|webp|gif|pdf)$/i.test(d.file_name || "") || (d.content_type || "") === "application/pdf";
  const saveAnnotation = async (file) => {
    setAnnBusy(true);
    const fd = new FormData(); fd.append("note", "Anotación sobre el plano"); fd.append("file", file);
    try { await api.post(`/projectdocs/${annotateFor.id}/revision`, fd); toast.success("Anotación guardada como nueva revisión"); setAnnotateFor(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAnnBusy(false); }
  };
  const load = () => api.get("/projectdocs").then((r) => setItems(r.data));
  const loadFolders = () => api.get("/projectdocs/folders").then((r) => setFolders(r.data)).catch(() => {});
  const loadCategories = () => api.get(`/doc-categories?section=${SECTION}`).then((r) => setCategories(r.data.map((c) => c.name))).catch(() => {});
  const addCategory = async (name) => { try { await api.post("/doc-categories", { section: SECTION, name }); loadCategories(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const deleteCategory = async (name) => { try { await api.delete(`/doc-categories?section=${SECTION}&name=${encodeURIComponent(name)}`); toast.success("Clasificación borrada de la lista"); loadCategories(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  useEffect(() => { load(); loadFolders(); loadCategories(); }, []);
  const createFolder = () => { setNewFolderName(""); setFolderModalOpen(true); };
  const submitFolder = async (e) => {
    e.preventDefault();
    const nm = newFolderName.trim();
    if (!nm) return toast.error("Escribe un nombre");
    const full = (folderFilter ? folderFilter + "/" : "") + nm;
    setFolderBusy(true);
    try {
      await api.post("/doc-categories", { section: SECTION, name: full });
      if (SECTION === "planos") await api.post("/projectdocs/folders", { name: full });
      toast.success("Carpeta creada"); setFolderModalOpen(false); loadFolders(); loadCategories();
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setFolderBusy(false); }
  };
  const moveDoc = async (id, name) => { try { await api.put(`/projectdocs/${id}/move`, { name }); toast.success("Plano movido"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const renameDoc = async (d) => { const nn = window.prompt("Nuevo nombre del documento:", d.name || ""); if (nn === null) return; const v = nn.trim(); if (!v) return; try { await api.put(`/projectdocs/${d.id}/rename`, { name: v }); toast.success("Nombre actualizado"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const renameFolderByPath = async (full) => {
    if (!full) return;
    const last = full.split("/").pop();
    const nn = window.prompt("Nuevo nombre de la carpeta:", last);
    if (!nn || !nn.trim() || nn.trim() === last) return;
    try {
      const r = await api.put("/projectdocs/folders", { name: full, new_name: nn.trim(), section: SECTION });
      toast.success("Carpeta renombrada");
      if (folderFilter === full || folderFilter.startsWith(full + "/")) setFolderFilter(r.data?.new || "");
      loadFolders(); loadCategories(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteFolderByPath = async (full) => {
    if (!full) return;
    if (!window.confirm(`¿Eliminar la carpeta "${full}" y sus subcarpetas? Los documentos quedarán en "Sin carpeta" (no se borran).`)) return;
    try {
      await api.delete(`/projectdocs/folders?name=${encodeURIComponent(full)}&section=${SECTION}`);
      toast.success("Carpeta eliminada");
      if (folderFilter === full || folderFilter.startsWith(full + "/")) setFolderFilter("");
      loadFolders(); loadCategories(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const renameFolder = () => renameFolderByPath(folderFilter);
  const deleteFolder = () => deleteFolderByPath(folderFilter);

  const save = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Selecciona un archivo");
    setBusy(true);
    const fd = new FormData();
    fd.append("name", form.name); fd.append("doc_group", form.doc_group); fd.append("specialty", form.specialty); fd.append("file", file);
    try {
      await api.post("/projectdocs", fd);
      toast.success("Documento subido (versión original)"); setOpen(false);
      setForm({ name: "", doc_group: group, specialty: "" }); setFile(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveRevision = async (e) => {
    e.preventDefault();
    if (!revFile) return toast.error("Selecciona el archivo de la nueva revisión");
    const fd = new FormData(); fd.append("note", revNote); fd.append("file", revFile);
    try {
      await api.post(`/projectdocs/${revFor.id}/revision`, fd);
      toast.success("Nueva revisión registrada — requiere re-aprobación"); setRevFor(null); setRevNote(""); setRevFile(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const approve = async (id) => { try { await api.post(`/projectdocs/${id}/approve`); toast.success("Documento aprobado"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const remove = async (id) => { if (!window.confirm("¿Eliminar documento y su historial?")) return; await api.delete(`/projectdocs/${id}`); load(); };

  const printDoc = (path) => {
    const w = window.open(fileUrl(path), "_blank");
    if (w) w.addEventListener("load", () => { try { w.print(); } catch (e) {} });
  };

  const usedSpecialties = [...new Set(items.filter((i) => i.doc_group === group).map((i) => (i.specialty || "").trim()).filter(Boolean))];
  const folderSource = SECTION === "planos" ? folders.map((f) => f.name) : [];
  const allPaths = [...new Set([...folderSource, ...usedSpecialties, ...categories])].sort();
  const prefix = folderFilter ? folderFilter + "/" : "";
  const children = [...new Set(allPaths.filter((p) => p.startsWith(prefix) && p.length > prefix.length).map((p) => p.slice(prefix.length).split("/")[0]))].sort();
  const crumbs = folderFilter ? folderFilter.split("/") : [];
  const shown = items.filter((i) => i.doc_group === group && (i.specialty || "") === folderFilter);

  const [dropActive, setDropActive] = useState(false);
  const onDragOverFiles = (e) => { if (Array.from(e.dataTransfer.types || []).includes("Files")) { e.preventDefault(); setDropActive(true); } };
  const onDropFiles = async (e) => {
    if (!(e.dataTransfer.files && e.dataTransfer.files.length)) {
      if (Array.from(e.dataTransfer.types || []).includes("Files")) { e.preventDefault(); setDropActive(false); toast.error("Arrastra los archivos desde el Explorador de archivos de Windows, no desde la barra de descargas del navegador."); }
      return;
    }
    e.preventDefault(); setDropActive(false);
    const fd = new FormData(); fd.append("doc_group", group); fd.append("specialty", "");
    [...e.dataTransfer.files].forEach((f) => fd.append("files", f));
    toast.loading("Subiendo a la bandeja general… 0%", { id: "drop" });
    try {
      await api.post("/projectdocs/bulk", fd, {
        onUploadProgress: (ev) => {
          const pct = ev.total ? Math.round((ev.loaded * 100) / ev.total) : 0;
          toast.loading(pct < 100 ? `Subiendo a la bandeja general… ${pct}%` : "Procesando archivos…", { id: "drop" });
        },
      });
      toast.success("Archivos subidos a la bandeja general (Todas › sin carpeta). Clasifícalos arrastrándolos a una carpeta.", { id: "drop" }); setFolderFilter(""); load();
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail), { id: "drop" }); }
  };
  return (
    <div onDragOver={onDragOverFiles} onDragLeave={() => setDropActive(false)} onDrop={onDropFiles} className="relative">
      {dropActive && (
        <div className="fixed inset-0 z-[60] bg-primary/10 border-4 border-dashed border-primary flex items-center justify-center pointer-events-none" data-testid="drop-overlay">
          <div className="bg-white rounded-md px-6 py-4 shadow-xl text-center"><div className="font-heading font-bold text-lg text-primary">Suelta los archivos aquí</div><div className="text-sm text-muted-foreground">Se subirán a la bandeja general para clasificarlos después</div></div>
        </div>
      )}
      <PageHeader overline={config.overline} title={config.title}
        action={isManager && (
          <div className="flex items-center gap-2">
            <a href={`${API}/projectdocs/zip`} data-testid="download-zip-btn"
              className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors duration-200">
              <FileText size={18} weight="bold" /> ZIP aprobados
            </a>
            <button onClick={() => { setScanForm({ name: "", doc_group: group, specialty: "" }); setScanFiles([]); setScanOpen(true); }} data-testid="scan-btn"
              className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors duration-200">
              <FileText size={18} weight="bold" /> Escanear a PDF
            </button>
            <button onClick={() => { setBulkForm({ doc_group: group, specialty: "" }); setBulkFiles([]); setBulkOpen(true); }} data-testid="bulk-upload-btn"
              className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors duration-200">
              <Plus size={18} weight="bold" /> Subir carpeta / varios
            </button>
            <PrimaryButton onClick={() => { setForm({ name: "", doc_group: group, specialty: "" }); setOpen(true); }} data-testid="add-projectdoc-btn"><Plus size={18} weight="bold" /> Subir documento</PrimaryButton>
          </div>
        )} />

      <Card className="p-4 mb-6 text-sm text-muted-foreground border-l-4 border-l-primary">
        Repositorio con <b className="text-foreground">control de cambios</b>: cada documento guarda su versión original y la trazabilidad de cada revisión (qué cambió, quién y cuándo). Los documentos requieren <b className="text-foreground">aprobación del Gerente General</b> para ser visibles a todos.
      </Card>

      <div className="flex flex-wrap gap-2 mb-6">
        {GROUPS.map((g) => (
          <button key={g.value} onClick={() => setGroup(g.value)} data-testid={`docgroup-${g.value}`}
            className={`px-4 py-1.5 rounded-sm text-sm font-medium border transition-colors duration-200 ${group === g.value ? "bg-primary text-white border-primary" : "bg-white border-input text-muted-foreground hover:bg-muted"}`}>
            {g.label} <span className="opacity-70">({items.filter((i) => i.doc_group === g.value).length})</span>
          </button>
        ))}
      </div>

      {(
        <div className="mb-6" data-testid="folder-bar">
          <div className="flex flex-wrap items-center gap-1 mb-2 text-xs font-semibold">
            <button onClick={() => setFolderFilter("")} data-testid="folder-all" onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) { moveDoc(dragId, ""); setDragId(null); } }} className={`hover:underline ${!folderFilter ? "text-foreground" : "text-primary"}`}>Todas</button>
            {crumbs.map((c, i) => {
              const path = crumbs.slice(0, i + 1).join("/");
              return (
                <span key={path} className="flex items-center gap-1">
                  <CaretRight size={11} weight="bold" className="text-muted-foreground" />
                  <button onClick={() => setFolderFilter(path)} onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) { moveDoc(dragId, path); setDragId(null); } }} className={`hover:underline ${i === crumbs.length - 1 ? "text-foreground" : "text-primary"}`}>{c}</button>
                </span>
              );
            })}
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-muted-foreground">{folderFilter ? "Subcarpetas" : "Carpetas"} (arrastra un documento a una carpeta):</span>
            <button onClick={() => setFoldersLayout((l) => (l === "h" ? "v" : "h"))} data-testid="toggle-folders-layout" title="Cambiar vista de carpetas"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-semibold border border-input text-muted-foreground hover:bg-muted transition-colors duration-200">
              {foldersLayout === "h" ? <><Rows size={13} weight="bold" /> Vista vertical</> : <><ListDashes size={13} weight="bold" /> Vista horizontal</>}
            </button>
          </div>
          <div className={foldersLayout === "h" ? "flex flex-row flex-nowrap items-center gap-2 overflow-x-auto pb-1" : "flex flex-col items-start gap-2"}>
            {!folderFilter && (
              <button onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) { moveDoc(dragId, ""); setDragId(null); } }} className="px-3 py-1 rounded-full text-xs font-semibold border border-dashed border-input text-muted-foreground">Sin carpeta</button>
            )}
            {children.map((n) => {
              const full = prefix + n;
              const count = items.filter((i) => i.doc_group === group && ((i.specialty || "") === full || (i.specialty || "").startsWith(full + "/"))).length;
              return (
                <div key={full} className="inline-flex items-center gap-0.5 rounded-full border bg-white border-input pr-1.5 hover:bg-muted transition-colors duration-200" onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragId) { moveDoc(dragId, full); setDragId(null); } }}>
                  <button onClick={() => setFolderFilter(full)} data-testid={`folder-${full}`} className="inline-flex items-center gap-1.5 pl-3 pr-1 py-1 text-xs font-semibold text-muted-foreground"><Folder size={13} weight="fill" className="text-primary" /> {n} <span className="opacity-70">({count})</span></button>
                  {isManager && <button onClick={(e) => { e.stopPropagation(); renameFolderByPath(full); }} data-testid={`rename-folder-${full}`} title="Renombrar carpeta" className="p-1 rounded-full text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors duration-200"><PencilSimple size={13} weight="bold" /></button>}
                  {canDelete && <button onClick={(e) => { e.stopPropagation(); deleteFolderByPath(full); }} data-testid={`delete-folder-${full}`} title="Eliminar carpeta" className="p-1 rounded-full text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors duration-200"><Trash size={13} weight="bold" /></button>}
                </div>
              );
            })}
            {isManager && <button onClick={createFolder} data-testid="new-folder-btn" className="px-3 py-1 rounded-full text-xs font-semibold border border-dashed border-primary text-primary hover:bg-primary/5 transition-colors duration-200">+ {folderFilter ? "Nueva subcarpeta" : "Nueva carpeta"}</button>}
            {folderFilter && isManager && <button onClick={renameFolder} data-testid="rename-folder-btn" className="px-3 py-1 rounded-full text-xs font-semibold border border-input text-muted-foreground hover:bg-muted transition-colors duration-200">Renombrar esta carpeta</button>}
            {folderFilter && canDelete && <button onClick={deleteFolder} data-testid="delete-folder-btn" className="px-3 py-1 rounded-full text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 transition-colors duration-200">Eliminar esta carpeta</button>}
          </div>
        </div>
      )}

      {shown.length === 0 ? (
        <EmptyState icon={Blueprint} title="Sin documentos en esta categoría" subtitle="Sube documentos con su nombre y especialidad; puedes cargar una carpeta completa de una vez."
          action={isManager && <PrimaryButton onClick={() => { setForm({ name: "", doc_group: group, specialty: "" }); setOpen(true); }}><Plus size={18} weight="bold" /> Subir documento</PrimaryButton>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((d) => (
            <Card key={d.id} data-testid={`projectdoc-${d.id}`} draggable onDragStart={() => setDragId(d.id)} className="p-5 flex flex-col cursor-move">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0"><FileText size={20} weight="bold" className="text-primary" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold leading-tight truncate">{d.name}</div>
                  {d.specialty && <div className="text-sm text-muted-foreground">{d.specialty}</div>}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600"><GitBranch size={13} weight="bold" /> Rev. {d.revision}</span>
                    {d.approved
                      ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-success"><SealCheck size={13} weight="fill" /> Aprobado</span>
                      : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600"><Clock size={13} weight="fill" /> Por aprobar</span>}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mt-4 pt-3 border-t border-border text-sm">
                <a href={fileUrl(d.file_path)} target="_blank" rel="noreferrer" data-testid={`view-${d.id}`} className="inline-flex items-center gap-1.5 font-medium text-primary hover:underline"><Eye size={16} weight="bold" /> Ver</a>
                <button onClick={() => printDoc(d.file_path)} className="inline-flex items-center gap-1.5 font-medium text-muted-foreground hover:text-foreground"><Printer size={16} weight="bold" /> Imprimir</button>
                <button onClick={() => setHistoryFor(d)} data-testid={`history-${d.id}`} className="inline-flex items-center gap-1.5 font-medium text-muted-foreground hover:text-foreground"><ClockCounterClockwise size={16} weight="bold" /> Cambios ({(d.versions || []).length})</button>
                {isManager && <button onClick={() => renameDoc(d)} data-testid={`rename-doc-${d.id}`} className="inline-flex items-center gap-1.5 font-medium text-muted-foreground hover:text-foreground"><PencilSimple size={16} weight="bold" /> Nombre</button>}
                {(d.versions || []).length >= 2 && <button onClick={() => openCompare(d)} data-testid={`compare-${d.id}`} className="inline-flex items-center gap-1.5 font-medium text-muted-foreground hover:text-foreground"><Columns size={16} weight="bold" /> Comparar</button>}
                {isManager && isImageDoc(d) && <button onClick={() => setAnnotateFor(d)} data-testid={`annotate-${d.id}`} className="inline-flex items-center gap-1.5 font-medium text-muted-foreground hover:text-foreground"><PaintBrush size={16} weight="bold" /> Anotar</button>}
              </div>
              {(isManager || canApprove) && (
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  {isManager && <button onClick={() => { setRevFor(d); setRevNote(""); setRevFile(null); }} data-testid={`revision-${d.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-slate-900 rounded-sm px-2.5 py-1.5 hover:bg-slate-800"><Plus size={13} weight="bold" /> Nueva revisión</button>}
                  {canApprove && !d.approved && <button onClick={() => approve(d.id)} data-testid={`approve-${d.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-success rounded-sm px-2.5 py-1.5 hover:opacity-90"><CheckCircle size={13} weight="fill" /> Aprobar</button>}
                  {isManager && (
                    <select data-testid={`move-${d.id}`} value={d.specialty || ""} onChange={(e) => moveDoc(d.id, e.target.value)} className="text-xs border border-input rounded-sm px-1.5 py-1 bg-white">
                      <option value="">Sin carpeta</option>
                      {allPaths.map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  )}
                  {canDelete && <button onClick={() => remove(d.id)} className="ml-auto p-1 text-muted-foreground hover:text-danger"><Trash size={16} /></button>}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Upload */}
      <Modal open={open} onClose={() => setOpen(false)} title="Subir documento del proyecto">
        <form onSubmit={save} className="space-y-4">
          <Field label="Categoría *">
            <select data-testid="projectdoc-group" className={inputClass} value={form.doc_group} onChange={(e) => setForm({ ...form, doc_group: e.target.value })}>
              {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </Field>
          <Field label="Nombre del documento *">
            <input data-testid="projectdoc-name" list="study-list" required className={inputClass} placeholder="Ej. Memoria de cálculo, Plano hidráulico N2…" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <datalist id="study-list">{SUGGESTIONS.map((s) => <option key={s} value={s} />)}</datalist>
          </Field>
          <Field label="Clasificación / carpeta"><CategoryField testid="projectdoc-specialty" categories={allPaths} value={form.specialty} onChange={(v) => setForm({ ...form, specialty: v })} onAddCategory={addCategory} onDeleteCategory={deleteCategory} deletableCategories={categories} /></Field>
          <Field label="Archivo *">
            <input data-testid="projectdoc-file" type="file" onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
          </Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-projectdoc-btn">{busy ? "Subiendo…" : "Subir"}</PrimaryButton>
        </form>
      </Modal>

      {/* New revision */}
      <Modal open={!!revFor} onClose={() => setRevFor(null)} title={`Nueva revisión · ${revFor?.name || ""}`}>
        {revFor && (
          <form onSubmit={saveRevision} className="space-y-4">
            <div className="text-sm text-muted-foreground">Revisión actual: <b className="text-foreground">Rev. {revFor.revision}</b> → nueva <b className="text-foreground">Rev. {revFor.revision + 1}</b>. Se conservará el historial y se requerirá re-aprobación.</div>
            <Field label="¿Qué cambió? (motivo / decisión) *"><input data-testid="revision-note" required className={inputClass} placeholder="Ej. Se recorrió columna eje 4 por interferencia hidráulica" value={revNote} onChange={(e) => setRevNote(e.target.value)} /></Field>
            <Field label="Archivo de la nueva revisión *">
              <input data-testid="revision-file" type="file" onChange={(e) => setRevFile(e.target.files[0])}
                className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
            </Field>
            <PrimaryButton type="submit" data-testid="save-revision-btn">Registrar revisión</PrimaryButton>
          </form>
        )}
      </Modal>

      {/* Change history */}
      <Modal open={!!historyFor} onClose={() => setHistoryFor(null)} title={`Control de cambios · ${historyFor?.name || ""}`}>
        {historyFor && (
          <div className="space-y-4">
            {[...(historyFor.versions || [])].reverse().map((v) => (
              <div key={v.rev} className="border-l-2 border-primary pl-3">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold">Rev. {v.rev}</span>
                  {v.rev === 1 && <span className="text-xs px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600 font-semibold">Original</span>}
                  {v.rev === historyFor.revision && <span className="text-xs px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 font-semibold">Vigente</span>}
                </div>
                <div className="text-sm mt-0.5">{v.note}</div>
                <div className="text-xs text-muted-foreground">{v.uploaded_by} · {new Date(v.created_at).toLocaleString("es-MX")}</div>
                <a href={fileUrl(v.file_path)} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 hover:underline mt-1"><FileText size={13} weight="fill" /> Ver esta versión</a>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Compare revisions */}
      <Modal open={!!compareFor} onClose={() => setCompareFor(null)} title={`Comparar revisiones · ${compareFor?.name || ""}`}>
        {compareFor && (() => {
          const vs = compareFor.versions || [];
          const va = vs.find((v) => v.rev === Number(revA));
          const vb = vs.find((v) => v.rev === Number(revB));
          const Pane = ({ v, side }) => (
            <div className="flex-1 min-w-0">
              <select data-testid={`compare-select-${side}`} className={inputClass + " mb-2"} value={side === "a" ? revA : revB} onChange={(e) => side === "a" ? setRevA(e.target.value) : setRevB(e.target.value)}>
                {vs.map((x) => <option key={x.rev} value={x.rev}>Rev. {x.rev}{x.rev === 1 ? " (Original)" : ""}</option>)}
              </select>
              {v && <>
                <iframe title={`rev-${v.rev}`} src={fileUrl(v.file_path)} className="w-full h-72 border border-border rounded-sm bg-muted" />
                <div className="text-xs text-muted-foreground mt-1">{v.note} · {v.uploaded_by}</div>
              </>}
            </div>
          );
          return (
            <div>
              <div className="flex gap-3">
                <Pane v={va} side="a" />
                <Pane v={vb} side="b" />
              </div>
              <p className="text-xs text-muted-foreground mt-3">Se muestran ambas versiones lado a lado. Usa los selectores para elegir qué revisiones comparar.</p>
            </div>
          );
        })()}
      </Modal>
      {/* Scan to PDF */}
      <Modal open={scanOpen} onClose={() => setScanOpen(false)} title="Escanear fotos a un PDF">
        <form onSubmit={saveScan} className="space-y-4">
          <Field label="Categoría *">
            <select className={inputClass} value={scanForm.doc_group} onChange={(e) => setScanForm({ ...scanForm, doc_group: e.target.value })}>
              {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </Field>
          <Field label="Nombre del documento *"><input data-testid="scan-name" required className={inputClass} value={scanForm.name} onChange={(e) => setScanForm({ ...scanForm, name: e.target.value })} /></Field>
          <Field label="Clasificación / carpeta"><CategoryField categories={allPaths} value={scanForm.specialty} onChange={(v) => setScanForm({ ...scanForm, specialty: v })} onAddCategory={addCategory} onDeleteCategory={deleteCategory} deletableCategories={categories} /></Field>
          <Field label="Fotos (se unirán en un PDF, en orden) *">
            <input data-testid="scan-files" type="file" accept="image/*" multiple onChange={(e) => setScanFiles(e.target.files)}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
          </Field>
          <PrimaryButton type="submit" disabled={scanBusy} data-testid="save-scan-btn">{scanBusy ? "Generando PDF…" : "Generar PDF"}</PrimaryButton>
        </form>
      </Modal>

      {/* Bulk upload (folder / multiple) */}
      <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title="Subir carpeta o varios documentos">
        <form onSubmit={saveBulk} className="space-y-4">
          <div className="text-sm text-muted-foreground">Sube muchos planos a la vez. Cada archivo se registra por separado usando su nombre de archivo, y todos quedan en la <b className="text-foreground">categoría y especialidad</b> que elijas aquí.</div>
          <Field label="Categoría *">
            <select data-testid="bulk-group" className={inputClass} value={bulkForm.doc_group} onChange={(e) => setBulkForm({ ...bulkForm, doc_group: e.target.value })}>
              {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </Field>
          <Field label="Clasificación / carpeta (se aplica a todos)"><CategoryField testid="bulk-specialty" categories={allPaths} value={bulkForm.specialty} onChange={(v) => setBulkForm({ ...bulkForm, specialty: v })} onAddCategory={addCategory} onDeleteCategory={deleteCategory} deletableCategories={categories} /></Field>
          <Field label="Selecciona una CARPETA completa *">
            <input data-testid="bulk-folder" type="file" webkitdirectory="" directory="" multiple onChange={(e) => setBulkFiles(e.target.files)}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-primary file:text-white file:rounded-sm file:font-medium" />
          </Field>
          <Field label="…o selecciona varios archivos sueltos">
            <input data-testid="bulk-files" type="file" multiple onChange={(e) => setBulkFiles(e.target.files)}
              className="w-full text-sm border border-dashed border-input rounded-sm p-3 file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-slate-900 file:text-white file:rounded-sm file:font-medium" />
          </Field>
          {bulkFiles.length > 0 && <div className="text-sm font-semibold text-primary">{bulkFiles.length} archivo(s) listo(s) para subir</div>}
          <PrimaryButton type="submit" disabled={bulkBusy} data-testid="save-bulk-btn">{bulkBusy ? "Subiendo…" : `Subir ${bulkFiles.length || ""} documento(s)`}</PrimaryButton>
        </form>
      </Modal>

      {/* Create folder / subfolder */}
      <Modal open={folderModalOpen} onClose={() => setFolderModalOpen(false)} title={folderFilter ? `Nueva subcarpeta en "${folderFilter}"` : "Nueva carpeta"}>
        <form onSubmit={submitFolder} className="space-y-4">
          <div className="text-sm text-muted-foreground">{folderFilter ? <>Se creará dentro de <b className="text-foreground">{folderFilter}</b>.</> : "Crea una carpeta de nivel raíz (ej. Estructural, Eléctrico). Podrás crear subcarpetas dentro."}</div>
          <Field label="Nombre de la carpeta *">
            <input data-testid="new-folder-name" autoFocus required className={inputClass} placeholder="Ej. Torre A, Nivel 1…" value={newFolderName} onChange={(e) => setNewFolderName(e.target.value)} />
          </Field>
          <PrimaryButton type="submit" disabled={folderBusy} data-testid="save-folder-btn">{folderBusy ? "Creando…" : "Crear carpeta"}</PrimaryButton>
        </form>
      </Modal>

      <AnnotationEditor open={!!annotateFor} onClose={() => setAnnotateFor(null)} storagePath={annotateFor?.file_path} onSave={saveAnnotation} saving={annBusy} />
    </div>
  );
}
